/**
 * bgmEngine.ts — Web Audio 芯片音关卡 BGM（L1~L9 每关独立音乐）
 * 纯 Oscillator/Gain 现场合成，零外部音频文件。
 *
 * 每关独立音乐要素（一线配乐规格）：
 *  - rootHz   调性中心（各关不同根音）
 *  - intervals 调式音程（小调/多利亚/弗里几亚/混合利底亚…）
 *  - leadWave / bassWave 主奏与贝斯波形（方波/脉冲/锯齿/三角）
 *  - lead     主旋律动机（16 步，各关不同 contour）
 *  - kick/bass/arp 节奏骨架（密度/速度递进）
 */

import { getAudioContext } from "./audioEngine.ts";

export type Step16 = readonly number[];

/** 一首关卡曲的纯数据图样（测试可逐字段断言） */
export interface BgmPattern {
  /** 速度 BPM（由浅入深） */
  bpm: number;
  /** 底鼓：1=击打，0=休止 */
  kick: Step16;
  /** 贝斯音级（0=休止，1..7 = 调式度数） */
  bass: Step16;
  /** 琶音音级（0=休止） */
  arp: Step16;
  /** 主旋律音级（0=休止）——每关独立动机 */
  lead: Step16;
  /** 琶音八度偏移（越高越亮） */
  arpOct: number;
  /** 调性中心 Hz（每关独立根音） */
  rootHz: number;
  /** 调式音程（相对根音的半音；长度 7） */
  intervals: readonly number[];
  /** 主奏波形（每关音色独立） */
  leadWave: OscillatorType;
  /** 贝斯波形 */
  bassWave: OscillatorType;
  /** 底鼓增益 0..1 */
  kickGain: number;
  /** 琶音增益 0..1 */
  arpGain: number;
  /** 主旋律增益 0..1 */
  leadGain: number;
}

/**
 * 每关独立调式音程（半音偏移）：
 * L1 自然小调 / L2 多利亚 / L3 弗里几亚 / L4 旋律小调 /
 * L5 和声小调 / L6 混合利底亚 / L7 洛克里亚色彩 / L8 双和声小调 / L9 弗里几亚主导
 */
const MODE_MINOR = [0, 2, 3, 5, 7, 8, 10] as const;
const MODE_DORIAN = [0, 2, 3, 5, 7, 9, 10] as const;
const MODE_PHRYGIAN = [0, 1, 3, 5, 7, 8, 10] as const;
const MODE_MELODIC = [0, 2, 3, 5, 7, 9, 11] as const;
const MODE_HARMONIC = [0, 2, 3, 5, 7, 8, 11] as const;
const MODE_MIXO = [0, 2, 4, 5, 7, 9, 10] as const;
const MODE_LOCRIAN = [0, 1, 3, 5, 6, 8, 10] as const;
const MODE_DOUBLE_HARM = [0, 1, 3, 4, 7, 8, 11] as const;

/** 按调式度数（1..7）+ 八度换算 Hz */
export function scaleHz(step: number, oct: number, rootHz: number, intervals: readonly number[]): number {
  if (step <= 0) return rootHz;
  const idx = (step - 1) % 7;
  const octAdd = Math.floor((step - 1) / 7);
  return rootHz * Math.pow(2, (intervals[idx] + (octAdd + oct) * 12) / 12);
}

/** L1~L9 图样：每关独立调性/调式/波形/主旋律 + 节奏密度递进 */
export const BGM_PATTERNS: readonly BgmPattern[] = [
  // L1 零度断崖 —— A 自然小调，三角主奏，稀疏空灵
  {
    bpm: 84,
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
    bass: [1, 0, 0, 0, 0, 0, 5, 0, 1, 0, 0, 0, 0, 0, 3, 0],
    arp: [0, 0, 8, 0, 0, 0, 10, 0, 0, 0, 8, 0, 0, 0, 7, 0],
    lead: [0, 0, 0, 5, 0, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 2],
    arpOct: 1,
    rootHz: 110.0,
    intervals: MODE_MINOR,
    leadWave: "triangle",
    bassWave: "triangle",
    kickGain: 0.22,
    arpGain: 0.07,
    leadGain: 0.11,
  },
  // L2 字节陷坑 —— B 多利亚，方波主奏，行军感
  {
    bpm: 92,
    kick: [1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0],
    bass: [1, 0, 1, 0, 5, 0, 0, 0, 1, 0, 1, 0, 4, 0, 0, 0],
    arp: [0, 8, 0, 10, 0, 8, 0, 7, 0, 8, 0, 10, 0, 12, 0, 10],
    lead: [1, 0, 3, 0, 5, 0, 3, 0, 1, 0, 5, 0, 4, 0, 3, 0],
    arpOct: 1,
    rootHz: 123.47,
    intervals: MODE_DORIAN,
    leadWave: "square",
    bassWave: "square",
    kickGain: 0.24,
    arpGain: 0.08,
    leadGain: 0.1,
  },
  // L3 七印走廊 —— C 弗里几亚，脉冲主奏，悬疑冷冽
  {
    bpm: 96,
    kick: [1, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 1, 0],
    bass: [1, 0, 0, 3, 0, 0, 5, 0, 1, 0, 0, 3, 0, 0, 7, 0],
    arp: [12, 0, 10, 0, 8, 0, 10, 0, 12, 0, 15, 0, 12, 0, 10, 0],
    lead: [0, 1, 0, 0, 3, 0, 0, 0, 0, 1, 0, 0, 2, 0, 0, 0],
    arpOct: 1,
    rootHz: 130.81,
    intervals: MODE_PHRYGIAN,
    leadWave: "square",
    bassWave: "triangle",
    kickGain: 0.2,
    arpGain: 0.1,
    leadGain: 0.09,
  },
  // L4 堆栈海啸 —— D 旋律小调，锯齿主奏，向上冲逃
  {
    bpm: 108,
    kick: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1],
    bass: [1, 1, 0, 5, 0, 5, 0, 0, 1, 1, 0, 5, 0, 3, 0, 4],
    arp: [8, 10, 12, 0, 10, 12, 15, 0, 8, 10, 12, 0, 15, 12, 10, 8],
    lead: [3, 5, 7, 8, 7, 5, 3, 2, 3, 5, 8, 9, 8, 7, 5, 3],
    arpOct: 1,
    rootHz: 146.83,
    intervals: MODE_MELODIC,
    leadWave: "sawtooth",
    bassWave: "square",
    kickGain: 0.26,
    arpGain: 0.09,
    leadGain: 0.1,
  },
  // L5 暗网绞杀战 —— E 和声小调，方波主奏，压迫防守
  {
    bpm: 112,
    kick: [1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 1, 0, 1, 0, 1],
    bass: [1, 0, 1, 0, 4, 0, 5, 0, 1, 0, 1, 0, 6, 0, 5, 0],
    arp: [10, 0, 12, 15, 0, 12, 0, 10, 10, 0, 12, 15, 0, 17, 0, 15],
    lead: [5, 0, 4, 0, 5, 0, 7, 0, 5, 0, 3, 0, 1, 0, 2, 0],
    arpOct: 1,
    rootHz: 164.81,
    intervals: MODE_HARMONIC,
    leadWave: "square",
    bassWave: "sawtooth",
    kickGain: 0.28,
    arpGain: 0.09,
    leadGain: 0.1,
  },
  // L6 清算轨道 —— F# 混合利底亚，锯齿主奏，追击高速
  {
    bpm: 120,
    kick: [1, 0, 1, 0, 1, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1],
    bass: [1, 0, 5, 0, 1, 0, 4, 0, 1, 0, 5, 0, 7, 0, 5, 0],
    arp: [12, 15, 12, 10, 12, 15, 17, 15, 12, 15, 12, 10, 8, 10, 12, 15],
    lead: [7, 5, 7, 9, 7, 5, 4, 5, 7, 9, 10, 9, 7, 5, 4, 2],
    arpOct: 2,
    rootHz: 185.0,
    intervals: MODE_MIXO,
    leadWave: "sawtooth",
    bassWave: "square",
    kickGain: 0.28,
    arpGain: 0.1,
    leadGain: 0.11,
  },
  // L7 深冻休眠舱 —— G 洛克里亚色彩，三角主奏，冷冽悬浮
  {
    bpm: 124,
    kick: [1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0],
    bass: [1, 0, 0, 1, 0, 0, 3, 0, 5, 0, 0, 5, 0, 4, 3, 0],
    arp: [15, 0, 12, 0, 15, 0, 17, 0, 15, 0, 12, 0, 10, 12, 15, 0],
    lead: [1, 0, 0, 2, 0, 0, 1, 0, 0, 3, 0, 0, 2, 0, 1, 0],
    arpOct: 2,
    rootHz: 196.0,
    intervals: MODE_LOCRIAN,
    leadWave: "triangle",
    bassWave: "triangle",
    kickGain: 0.26,
    arpGain: 0.11,
    leadGain: 0.1,
  },
  // L8 死锁巨像 —— A 双和声小调，锯齿主奏，Boss 重压
  {
    bpm: 128,
    kick: [1, 1, 0, 1, 1, 0, 1, 1, 1, 1, 0, 1, 1, 0, 1, 1],
    bass: [1, 0, 1, 0, 1, 0, 5, 0, 1, 0, 1, 0, 6, 0, 5, 4],
    arp: [12, 15, 17, 15, 12, 15, 19, 17, 12, 15, 17, 15, 12, 10, 8, 10],
    lead: [1, 2, 1, 0, 1, 0, 5, 4, 1, 2, 1, 0, 6, 5, 4, 2],
    arpOct: 2,
    rootHz: 110.0,
    intervals: MODE_DOUBLE_HARM,
    leadWave: "sawtooth",
    bassWave: "sawtooth",
    kickGain: 0.3,
    arpGain: 0.11,
    leadGain: 0.12,
  },
  // L9 创世神枢王座 —— Bb 弗里几亚主导，方波+锯齿，终局史诗
  {
    bpm: 136,
    kick: [1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 1],
    bass: [1, 1, 0, 1, 5, 5, 0, 5, 1, 1, 0, 1, 7, 6, 5, 4],
    arp: [17, 15, 12, 15, 17, 19, 17, 15, 17, 15, 12, 15, 19, 17, 15, 12],
    lead: [8, 7, 8, 10, 8, 7, 5, 7, 8, 10, 12, 10, 8, 7, 5, 4],
    arpOct: 2,
    rootHz: 116.54,
    intervals: MODE_PHRYGIAN,
    leadWave: "square",
    bassWave: "sawtooth",
    kickGain: 0.32,
    arpGain: 0.12,
    leadGain: 0.12,
  },
];

/** 按关卡 id（1..9）取图样；0 = 枢纽用 L1；越界钳到首尾 */
export function bgmPatternForLevel(level: number): BgmPattern {
  const i = Math.max(0, Math.min(BGM_PATTERNS.length - 1, Math.floor(level) - 1));
  return BGM_PATTERNS[level >= 1 ? i : 0];
}

/** 派生：一首曲的总时长（秒），测试可验 bpm 合法 */
export function bgmLoopSeconds(p: BgmPattern): number {
  return (60 / p.bpm) * 4; // 4/4 一小节 ×16 步=4 拍
}

/** 派生：每关主旋律指纹（lead 非零步的音级序列），测试可验各关互不相同 */
export function leadMotif(p: BgmPattern): string {
  return p.lead.filter((v) => v > 0).join(",");
}

export interface BgmStatus {
  playing: boolean;
  level: number;
  bpm: number;
  volume: number;
}

class BgmEngine {
  private master: GainNode | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private nextNoteTime = 0;
  private step = 0;
  private pattern: BgmPattern = BGM_PATTERNS[0];
  private level = 1;
  private playing = false;
  private volume = 0.22;
  private enabled = true;

  status(): BgmStatus {
    return {
      playing: this.playing,
      level: this.level,
      bpm: this.pattern.bpm,
      volume: this.volume,
    };
  }

  async unlock(): Promise<void> {
    try {
      const ctx = getAudioContext();
      if (!ctx) return;
      if (!this.master) {
        this.master = ctx.createGain();
        this.master.gain.value = this.enabled ? this.volume : 0;
        this.master.connect(ctx.destination);
      }
      if (ctx.state === "suspended") await ctx.resume();
    } catch {
      /* 降级 */
    }
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    try {
      const ctx = getAudioContext();
      if (this.master && ctx) {
        this.master.gain.setTargetAtTime(
          on ? this.volume : 0,
          ctx.currentTime,
          0.02,
        );
      }
    } catch {
      /* 降级 */
    }
    if (!on) this.stop();
  }

  setVolume(v: number): void {
    this.volume = Math.min(1, Math.max(0, v));
    try {
      const ctx = getAudioContext();
      if (this.master && ctx && this.enabled) {
        this.master.gain.setTargetAtTime(this.volume, ctx.currentTime, 0.02);
      }
    } catch {
      /* 降级 */
    }
  }

  async setLevel(level: number): Promise<void> {
    this.level = Math.max(1, Math.min(9, Math.floor(level) || 1));
    this.pattern = bgmPatternForLevel(this.level);
    this.step = 0;
    // 关键修复：必须 stop()（置 playing=false + 清定时器）再 start()。
    // 旧实现只 stopScheduler() 不置 playing，导致 start() 见 playing=true 直接 return，
    // 调度器永久停死——这正是「过关后下一关音乐没有了」的根因。
    if (this.playing || this.timer != null) {
      this.stop();
      await this.start();
    }
  }

  /**
   * 切关原子入口：设定关卡图样并保证 BGM 在播（过关递进 / 开局统一走这里）。
   * 幂等：已在播同关也安全。
   */
  async ensureLevelPlaying(level: number): Promise<void> {
    this.level = Math.max(1, Math.min(9, Math.floor(level) || 1));
    this.pattern = bgmPatternForLevel(this.level);
    this.step = 0;
    this.stop();
    await this.start();
  }

  async start(): Promise<void> {
    await this.unlock();
    if (!this.enabled) return;
    // 自愈：playing=true 但定时器已死（状态泄漏）时仍重启调度器
    if (this.playing && this.timer != null) return;
    const ctx = getAudioContext();
    if (!ctx || !this.master) return;
    this.stopScheduler();
    this.playing = true;
    this.nextNoteTime = ctx.currentTime + 0.05;
    this.step = 0;
    this.timer = setInterval(() => this.scheduler(), 40);
    this.scheduler();
  }

  stop(): void {
    this.playing = false;
    this.stopScheduler();
  }

  private stopScheduler(): void {
    if (this.timer != null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private scheduler(): void {
    const ctx = getAudioContext();
    if (!ctx || !this.master || !this.playing) return;
    const p = this.pattern;
    const stepDur = 60 / p.bpm / 4;
    while (this.nextNoteTime < ctx.currentTime + 0.12) {
      this.scheduleStep(this.step, this.nextNoteTime);
      this.nextNoteTime += stepDur;
      this.step = (this.step + 1) % 16;
    }
  }

  private scheduleStep(step: number, t: number): void {
    const ctx = getAudioContext();
    if (!ctx || !this.master) return;
    const p = this.pattern;
    const hz = (s: number, oct = 0) => scaleHz(s, oct, p.rootHz, p.intervals);

    // 低频底鼓：三角波音高下扫
    if (p.kick[step]) {
      this.tone({
        type: "triangle",
        freq: 140,
        endFreq: 42,
        start: t,
        duration: 0.12,
        gain: p.kickGain,
      });
    }

    // 贝斯：每关波形
    const b = p.bass[step];
    if (b) {
      this.tone({
        type: p.bassWave,
        freq: hz(b),
        start: t,
        duration: 0.1,
        gain: 0.1,
      });
    }

    // 高频琶音
    const a = p.arp[step];
    if (a) {
      this.tone({
        type: "square",
        freq: hz(a, p.arpOct),
        start: t,
        duration: 0.06,
        gain: p.arpGain,
      });
      this.tone({
        type: "square",
        freq: hz(a, p.arpOct) * 1.5,
        start: t,
        duration: 0.035,
        gain: p.arpGain * 0.35,
      });
    }

    // 主旋律动机：每关独立 lead 线 + 波形
    const l = p.lead[step];
    if (l) {
      this.tone({
        type: p.leadWave,
        freq: hz(l, 1),
        start: t,
        duration: 0.14,
        gain: p.leadGain,
      });
    }
  }

  private tone(opts: {
    type: OscillatorType;
    freq: number;
    endFreq?: number;
    start: number;
    duration: number;
    gain: number;
  }): void {
    const ctx = getAudioContext();
    if (!ctx || !this.master) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = opts.type;
    osc.frequency.setValueAtTime(opts.freq, opts.start);
    if (opts.endFreq && opts.endFreq !== opts.freq) {
      osc.frequency.exponentialRampToValueAtTime(opts.endFreq, opts.start + opts.duration);
    }
    g.gain.setValueAtTime(0, opts.start);
    g.gain.linearRampToValueAtTime(opts.gain, opts.start + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, opts.start + opts.duration);
    osc.connect(g);
    g.connect(this.master);
    osc.start(opts.start);
    osc.stop(opts.start + opts.duration + 0.02);
  }
}

export const bgmEngine = new BgmEngine();
